import {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react'
import qs from 'qs'
import {ID, QueryResponseContextProps, QueryState} from './models'

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

function stringifyRequestQuery(state: QueryState): string {
  const pagination = qs.stringify(state, {filter: ['page', 'pageSize'], skipNulls: true})
  const sort = qs.stringify(state, {filter: ['sort', 'order'], skipNulls: true})
  const search = isNotEmpty(state.search)
    ? qs.stringify(state, {filter: ['search'], skipNulls: true})
    : ''

  const userFilter = qs.stringify(state, {
    filter: ['onlyDeletedUser', 'onlyNormalUser'],
    skipNulls: true,
  })

  let filter = state.filter
    ? Object.entries(state.filter as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          if (obj[1] == true || obj[1] == false) {
            if (obj[1] == true) {
              return `${obj[0]}=${obj[1]}`
            }
          } else {
            return `${obj[0]}=${obj[1]}`
          }
          //return `filter_${obj[0]}=${obj[1]}`
        })
        .join('&')
    : ''

  let sortUpdate = sort.split('&')
  let orderWith = sortUpdate[0].split('=')
  let finalSortingString = sort.length === 0 ? '' : 'orderBy=' + orderWith[1] + '&' + sortUpdate[1]
  let paginationUpdate = pagination.split('&')
  if (paginationUpdate.length === 1) {
    paginationUpdate.push('pageSize=100')
  }
  let pageSize = paginationUpdate[1].split('=')
  let finalPaginationString = paginationUpdate[0] + '&pageSize=' + pageSize[1]

  if (finalSortingString.length !== 0) {
    filter = filter.replace(/&orderBy=userId&?/, '&') // Replace the pattern with an empty string or another delimiter
  }

  return JSON.stringify([finalPaginationString, finalSortingString, search, userFilter, filter])
  // .filter((f) => f)
  // .join('&')
  //.toLowerCase()
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & {user_id?: ID; unique_user_id?: ID}>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected(
    data
      .filter((item) => item.unique_user_id || item.user_id)
      .map((item) => item.unique_user_id || item.user_id)
  )
}
// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  isNotEmpty,
}
